import { capitalize } from "lodash";
import { enums } from "../../shim/jsbridge";
import {
  EventTypeGroups,
  ITypeOptionsClassificationChange,
  ITypeOptionsSampleAvailable,
  ITypeOptionsYaraMatch
} from "./@types";
import { MenuItems } from "../../components/@types";

export const DELIVERY_METHOD_DEFAULT_VALUES = {
  id: null,
  interval: 60,
  name: "",
  type: "",
  type_options: {}
};

export const DELIVERY_METHOD_TYPE = {
  email: {
    name: "email",
    label: "E-mail",
    interval: true,
    defaultOptions: {
      to_user: true,
      to: []
    }
  },
  syslog: {
    name: "syslog",
    label: "Syslog",
    interval: false,
    defaultOptions: {
      host: "",
      port: "",
      transport_protocol: "",
      format: "{sha1} - {alert_type} - {description}"
    }
  }
};

export const ALERT_TYPES_MAP = {
  CLASSIFICATION_CHANGE: "classification_change",
  SAMPLE_AVAILABLE: "sample_available",
  YARA_MATCH: "yara_match",
  SPECTRA_INTELLIGENCE_ANALYSIS_COMPLETE: "ticloud_analysis_complete",
  SPECTRA_INTELLIGENCE_UPLOAD_COMPLETE: "ticloud_upload_complete",
  CUCKOO_ANALYSIS_STARTED: "cuckoo_analysis_started",
  CUCKOO_ANALYSIS_FINISHED: "cuckoo_analysis_finished",
  CUCKOO_ANALYSIS_FAILED: "cuckoo_analysis_failed",
  STATIC_ANALYSIS_STARTED: "static_analysis_started",
  STATIC_ANALYSIS_FINISHED: "static_analysis_finished",
  STATIC_ANALYSIS_FAILED: "static_analysis_failed",
  RLCS_ANALYSIS_STARTED: "rlcs_analysis_started",
  RLCS_ANALYSIS_FINISHED: "rlcs_analysis_finished",
  RLCS_ANALYSIS_FAILED: "rlcs_analysis_failed",
  AUXILIARY_ANALYSIS_STARTED: "auxiliary_analysis_started",
  AUXILIARY_ANALYSIS_FINISHED: "auxiliary_analysis_finished",
  AUXILIARY_ANALYSIS_FAILED: "auxiliary_analysis_failed",
  JOE_ANALYSIS_STARTED: "joe_analysis_started",
  JOE_ANALYSIS_FINISHED: "joe_analysis_finished",
  JOE_ANALYSIS_FAILED: "joe_analysis_failed",
  CAPE_ANALYSIS_STARTED: "cape_analysis_started",
  CAPE_ANALYSIS_FINISHED: "cape_analysis_finished",
  CAPE_ANALYSIS_FAILED: "cape_analysis_failed",
  CISCO_SMA_ANALYSIS_STARTED: "cisco_sma_analysis_started",
  CISCO_SMA_ANALYSIS_FINISHED: "cisco_sma_analysis_finished",
  CISCO_SMA_ANALYSIS_FAILED: "cisco_sma_analysis_failed",
  VMRAY_ANALYSIS_STARTED: "vmray_analysis_started",
  VMRAY_ANALYSIS_FINISHED: "vmray_analysis_finished",
  VMRAY_ANALYSIS_FAILED: "vmray_analysis_failed",
  FIREEYE_ANALYSIS_STARTED: "fireeye_analysis_started",
  FIREEYE_ANALYSIS_FINISHED: "fireeye_analysis_finished",
  FIREEYE_ANALYSIS_FAILED: "fireeye_analysis_failed"
} as const;

export const ANALYSIS_ALERT_ITEMS = {
  STATIC_ANALYSIS: {
    label: "Static Analysis",
    icon: "static-analysis",
    children: [
      {
        label: "Started",
        value: ALERT_TYPES_MAP.STATIC_ANALYSIS_STARTED
      },
      {
        label: "Finished",
        value: ALERT_TYPES_MAP.STATIC_ANALYSIS_FINISHED
      },
      {
        label: "Failed",
        value: ALERT_TYPES_MAP.STATIC_ANALYSIS_FAILED
      }
    ]
  },
  THREAT_INTELLIGENCE: {
    label: "Threat Intelligence",
    icon: "cloud-intelligence",
    children: [
      {
        label: "Started",
        value: ALERT_TYPES_MAP.SPECTRA_INTELLIGENCE_UPLOAD_COMPLETE
      },
      {
        label: "Finished",
        value: ALERT_TYPES_MAP.SPECTRA_INTELLIGENCE_ANALYSIS_COMPLETE
      }
    ]
  },
  CLOUD_SANDBOX: {
    label: "RL Cloud Sandbox",
    icon: "dynamic-analysis",
    children: [
      {
        label: "Started",
        value: ALERT_TYPES_MAP.RLCS_ANALYSIS_STARTED
      },
      {
        label: "Finished",
        value: ALERT_TYPES_MAP.RLCS_ANALYSIS_FINISHED
      },
      {
        label: "Failed",
        value: ALERT_TYPES_MAP.RLCS_ANALYSIS_FAILED
      }
    ]
  },
  AUXILIARY_ANALYSIS: {
    label: "RL Auxiliary Analysis",
    icon: "auxiliary-analysis",
    children: [
      {
        label: "Started",
        value: ALERT_TYPES_MAP.AUXILIARY_ANALYSIS_STARTED
      },
      {
        label: "Finished",
        value: ALERT_TYPES_MAP.AUXILIARY_ANALYSIS_FINISHED
      },
      {
        label: "Failed",
        value: ALERT_TYPES_MAP.AUXILIARY_ANALYSIS_FAILED
      }
    ]
  },
  JOE_SANDBOX: {
    label: "Joe Sandbox",
    icon: "joe",
    children: [
      {
        label: "Started",
        value: ALERT_TYPES_MAP.JOE_ANALYSIS_STARTED
      },
      {
        label: "Finished",
        value: ALERT_TYPES_MAP.JOE_ANALYSIS_FINISHED
      },
      {
        label: "Failed",
        value: ALERT_TYPES_MAP.JOE_ANALYSIS_FAILED
      }
    ]
  },
  CUCKOO: {
    label: "Cuckoo",
    icon: "cuckoo",
    children: [
      {
        label: "Started",
        value: ALERT_TYPES_MAP.CUCKOO_ANALYSIS_STARTED
      },
      {
        label: "Finished",
        value: ALERT_TYPES_MAP.CUCKOO_ANALYSIS_FINISHED
      },
      {
        label: "Failed",
        value: ALERT_TYPES_MAP.CUCKOO_ANALYSIS_FAILED
      }
    ]
  },
  CAPE: {
    label: "Cape",
    icon: "cape",
    children: [
      {
        label: "Started",
        value: ALERT_TYPES_MAP.CAPE_ANALYSIS_STARTED
      },
      {
        label: "Finished",
        value: ALERT_TYPES_MAP.CAPE_ANALYSIS_FINISHED
      },
      {
        label: "Failed",
        value: ALERT_TYPES_MAP.CAPE_ANALYSIS_FAILED
      }
    ]
  },
  CISCO_SECURE_MALWARE_ANALYTICS: {
    label: "Cisco Secure Malware Analytics",
    icon: "cisco_secure_malware_analytics",
    children: [
      {
        label: "Started",
        value: ALERT_TYPES_MAP.CISCO_SMA_ANALYSIS_STARTED
      },
      {
        label: "Finished",
        value: ALERT_TYPES_MAP.CISCO_SMA_ANALYSIS_FINISHED
      },
      {
        label: "Failed",
        value: ALERT_TYPES_MAP.CISCO_SMA_ANALYSIS_FAILED
      }
    ]
  },
  VMRAY: {
    label: "VMRay",
    icon: "vmray_tcbase",
    children: [
      {
        label: "Started",
        value: ALERT_TYPES_MAP.VMRAY_ANALYSIS_STARTED
      },
      {
        label: "Finished",
        value: ALERT_TYPES_MAP.VMRAY_ANALYSIS_FINISHED
      },
      {
        label: "Failed",
        value: ALERT_TYPES_MAP.VMRAY_ANALYSIS_FAILED
      }
    ]
  },
  FIREEYE: {
    label: "FireEye",
    icon: "fireeye",
    children: [
      {
        label: "Started",
        value: ALERT_TYPES_MAP.FIREEYE_ANALYSIS_STARTED
      },
      {
        label: "Finished",
        value: ALERT_TYPES_MAP.FIREEYE_ANALYSIS_FINISHED
      },
      {
        label: "Failed",
        value: ALERT_TYPES_MAP.FIREEYE_ANALYSIS_FAILED
      }
    ]
  }
};

export const EVENT_TYPE_GROUPS = {
  ANALYSIS_ALERTS: {
    name: "analysis_alerts",
    label: "Analysis Alerts",
    alert_types: [
      ALERT_TYPES_MAP.SPECTRA_INTELLIGENCE_ANALYSIS_COMPLETE,
      ALERT_TYPES_MAP.SPECTRA_INTELLIGENCE_UPLOAD_COMPLETE,
      ALERT_TYPES_MAP.STATIC_ANALYSIS_STARTED,
      ALERT_TYPES_MAP.STATIC_ANALYSIS_FINISHED,
      ALERT_TYPES_MAP.STATIC_ANALYSIS_FAILED,
      ALERT_TYPES_MAP.RLCS_ANALYSIS_STARTED,
      ALERT_TYPES_MAP.RLCS_ANALYSIS_FINISHED,
      ALERT_TYPES_MAP.RLCS_ANALYSIS_FAILED,
      ALERT_TYPES_MAP.AUXILIARY_ANALYSIS_STARTED,
      ALERT_TYPES_MAP.AUXILIARY_ANALYSIS_FINISHED,
      ALERT_TYPES_MAP.AUXILIARY_ANALYSIS_FAILED,
      ALERT_TYPES_MAP.JOE_ANALYSIS_STARTED,
      ALERT_TYPES_MAP.JOE_ANALYSIS_FINISHED,
      ALERT_TYPES_MAP.JOE_ANALYSIS_FAILED,
      ALERT_TYPES_MAP.CUCKOO_ANALYSIS_STARTED,
      ALERT_TYPES_MAP.CUCKOO_ANALYSIS_FINISHED,
      ALERT_TYPES_MAP.CUCKOO_ANALYSIS_FAILED,
      ALERT_TYPES_MAP.CAPE_ANALYSIS_STARTED,
      ALERT_TYPES_MAP.CAPE_ANALYSIS_FINISHED,
      ALERT_TYPES_MAP.CAPE_ANALYSIS_FAILED,
      ALERT_TYPES_MAP.CISCO_SMA_ANALYSIS_STARTED,
      ALERT_TYPES_MAP.CISCO_SMA_ANALYSIS_FINISHED,
      ALERT_TYPES_MAP.CISCO_SMA_ANALYSIS_FAILED,
      ALERT_TYPES_MAP.VMRAY_ANALYSIS_STARTED,
      ALERT_TYPES_MAP.VMRAY_ANALYSIS_FINISHED,
      ALERT_TYPES_MAP.VMRAY_ANALYSIS_FAILED,
      ALERT_TYPES_MAP.FIREEYE_ANALYSIS_STARTED,
      ALERT_TYPES_MAP.FIREEYE_ANALYSIS_FINISHED,
      ALERT_TYPES_MAP.FIREEYE_ANALYSIS_FAILED
    ]
  },
  YARA_MATCH: {
    name: "yara",
    label: "YARA Matches",
    alert_types: [ALERT_TYPES_MAP.YARA_MATCH]
  },
  SAMPLE_AVAILABLE: {
    name: "sample_available",
    label: "Sample Available for Download",
    alert_types: [ALERT_TYPES_MAP.SAMPLE_AVAILABLE]
  },
  CLASSIFICATION_CHANGE: {
    name: "classification_change",
    label: "Classification Changes",
    alert_types: [ALERT_TYPES_MAP.CLASSIFICATION_CHANGE]
  }
} as EventTypeGroups;

export const DEFAULT_EVENT_TYPE_OPTIONS = {
  classification_change: {
    from_threat_status: ["unknown", "goodware", "suspicious", "malicious"],
    threat_status: ["suspicious", "malicious"],
    threat_level_change: true,
    threat_name_change: true
  } as ITypeOptionsClassificationChange,
  sample_available: {
    fetch_and_analyze: false
  } as ITypeOptionsSampleAvailable,
  yara_match: {
    rulesets: []
  } as ITypeOptionsYaraMatch,
  cuckoo_analysis_started: {},
  cuckoo_analysis_finished: {},
  cuckoo_analysis_failed: {},
  cape_analysis_started: {},
  cape_analysis_finished: {},
  cape_analysis_failed: {},
  joe_analysis_started: {},
  joe_analysis_finished: {},
  joe_analysis_failed: {},
  auxiliary_analysis_started: {},
  auxiliary_analysis_finished: {},
  auxiliary_analysis_failed: {},
  cisco_sma_analysis_started: {},
  cisco_sma_analysis_finished: {},
  cisco_sma_analysis_failed: {},
  vmray_analysis_started: {},
  vmray_analysis_finished: {},
  vmray_analysis_failed: {},
  fireeye_analysis_started: {},
  fireeye_analysis_finished: {},
  fireeye_analysis_failed: {},
  ticloud_analysis_complete: {},
  ticloud_upload_complete: {}
};

export const THREAT_STATUS_OPTIONS: MenuItems = Object.entries(
  enums.threat_status
).map(([k, _]) => ({
  value: k.toLowerCase(),
  label: capitalize(k)
}));

export const DEFAULT_DELIVERY_INTERVAL_VALUE = 60;

export const ACTION_INTERVAL_CHOICES = [
  { value: 60, label: "1 minute" },
  { value: 60 * 5, label: "5 minutes" },
  { value: 60 * 10, label: "10 minutes" },
  { value: 60 * 15, label: "15 minutes" },
  { value: 60 * 30, label: "30 minutes" },
  { value: 60 * 60, label: "1 hour" },
  { value: 60 * 60 * 2, label: "2 hours" },
  { value: 60 * 60 * 4, label: "4 hours" },
  { value: 60 * 60 * 6, label: "6 hours" },
  { value: 60 * 60 * 8, label: "8 hours" },
  { value: 60 * 60 * 12, label: "12 hours" },
  { value: 60 * 60 * 24, label: "24 hours" }
];

export const TRANSPORT_PROTOCOL_OPTIONS: MenuItems = [
  { value: "", label: "Select Protocol" },
  { value: "TCP", label: "TCP" },
  { value: "UDP", label: "UDP" }
];

export const DEFAULT_GRID_PAGE_SELECTIONS = [
  { label: "10", value: "10" },
  { label: "20", value: "20" },
  { label: "50", value: "50" }
];

export const DEFAULT_GRID_PAGE_SIZE = 10;

export const INITIAL_PAGING = {
  currentPage: 1,
  pageSize: DEFAULT_GRID_PAGE_SIZE
};

export const SUBSCRIPTION_ACTIONS = {
  SUBSCRIBE: "subscribe",
  UNSUBSCRIBE: "unsubscribe"
};

export const SUBSCRIPTION_URL_PARAMS = {
  HASHES: "use_hashes",
  YARA: "use_yara"
};

export const MAX_SELECT_HASHES = 100;
